import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { MONTHS, YEARS, money, todayParts } from "../../constants";
import {
  Button,
  DataCard,
  Empty,
  Icon,
  PageTitle,
  Select,
  Spinner,
  Stat,
  Td,
  Th
} from "../../components/ui";

const DAY_COLS = Array.from({ length: 31 }, (_, i) => i + 1); // [1..31]

function fmtDate(val) {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.toLocaleDateString("en-GB");
}

export default function MyPayslip({ user, setToast }) {
  const today = todayParts();

  // Synchronized month + year state
  const [filters, setFilters] = useState({
    year: today.year,
    month: today.month  // Full month name, e.g. "May"
  });

  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  // Re-fetch whenever month or year changes
  async function load() {
    setLoading(true);
    try {
      const res = await api.getTrainerPayslipDynamic({
        userEmail: user.email,
        year: filters.year,
        month: filters.month   // backend resolves name → number
      });
      setData(res);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Failed to load payslip" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [filters.month, filters.year]);

  const canDownload = !!(data?.success && (data.summary || (data.salaryRows || []).length));

  async function downloadPdf() {
    if (!canDownload) return;
    try {
      const { generatePayslipPdf } = await import("../../utils/payslipPdf");
      const monthNum = data.month || (MONTHS.indexOf(filters.month) + 1);
      await generatePayslipPdf({
        trainerName: data.trainerName,
        monthName: MONTHS[(monthNum || 1) - 1] || filters.month,
        year: data.year || filters.year,
        month: monthNum,
        summary: data.summary || { base: 0, incentives: 0, deductions: 0, net: 0, status: "Draft" },
        transactions: data.transactions || []
      });
    } catch (err) {
      setToast({ type: "error", message: "Gagal membuat PDF: " + (err.message || err) });
    }
  }

  return (
    <>
      <PageTitle
        title="My Payslip"
        subtitle={`Salary record for ${filters.month} ${filters.year}.`}
        action={
          <div className="flex flex-wrap gap-2">
            <Select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="w-28"
            >
              {YEARS.map((y) => <option key={y}>{y}</option>)}
            </Select>
            <Select
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              className="w-40"
            >
              {MONTHS.map((m) => <option key={m}>{m}</option>)}
            </Select>
            <Button variant="success" onClick={downloadPdf} disabled={!canDownload}>
              <Icon name="picture_as_pdf" />
              PDF
            </Button>
          </div>
        }
      />

      {loading ? (
        <Spinner />
      ) : !data?.success ? (
        <Empty label={`No salary data available for ${filters.month} ${filters.year}.`} />
      ) : (
        <PayslipView data={data} />
      )}
    </>
  );
}

function PayslipView({ data }) {
  const { trainerName, summary, salaryRows = [], transactions = [], month, year } = data;
  const monthName = MONTHS[(month || 1) - 1] || "";

  if (!summary && salaryRows.length === 0) {
    return (
      <Empty label={`No finalized salary data for ${monthName} ${year}.`} />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header identity card */}
      <DataCard className="p-6">
        <div className="mb-4 flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary font-black text-xl text-white">
            {String(trainerName || "?")[0].toUpperCase()}
          </div>
          <div>
            <p className="text-xl font-black">{trainerName}</p>
            <p className="text-sm text-slate-500">{monthName} {year} · Payslip</p>
          </div>
        </div>

        {summary ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat label="Base Shift Earnings" value={money(summary.base)} />
            <Stat label="Incentives" value={money(summary.incentives)} good />
            <Stat label="Deductions" value={money(summary.deductions)} bad />
            <Stat label="Net Take-Home Pay" value={money(summary.net)} primary />
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-400">
            Final salary not yet calculated for this month. Showing shift earnings only.
          </div>
        )}
      </DataCard>

      {/* Per-session daily breakdown */}
      {salaryRows.length > 0 && (
        <div>
          <h3 className="mb-3 text-lg font-black">Daily Shift Breakdown</h3>
          {salaryRows.map((row, idx) => (
            <SessionBreakdownCard key={idx} row={row} monthName={monthName} year={year} />
          ))}
        </div>
      )}

      {/* Transactions */}
      {transactions.length > 0 && (
        <DataCard>
          <div className="border-b border-slate-100 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-800/50 rounded-t-2xl">
            <h3 className="font-black">Transaction Details</h3>
          </div>
          <table className="w-full min-w-[520px] whitespace-nowrap text-left">
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Type</Th>
                <Th>Category</Th>
                <Th right>Amount</Th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx, i) => (
                <tr key={i} className="border-t border-slate-200 dark:border-slate-800">
                  <Td>{fmtDate(tx.date)}</Td>
                  <Td>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-black ${
                      tx.type === "incentive"
                        ? "bg-green-500/15 text-green-600"
                        : "bg-red-500/15 text-red-600"
                    }`}>
                      {tx.type}
                    </span>
                  </Td>
                  <Td>{tx.category}</Td>
                  <Td right className="font-black">{money(tx.amount)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataCard>
      )}
    </div>
  );
}

function SessionBreakdownCard({ row, monthName, year }) {
  const activeDays = row.days.filter((d) => d > 0);

  return (
    <DataCard className="mb-4">
      {/* Card header */}
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-800/50">
        <div>
          <p className="flex items-center gap-2 font-black">
            {row.session}
            {row.jenis === "staff" && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-black text-primary">
                sesi staff
              </span>
            )}
          </p>
          <p className="text-xs text-slate-500">{row.position}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400">Month Total</p>
          <p className="font-black text-primary">{money(row.total)}</p>
        </div>
      </div>

      {/* Daily cells */}
      <div className="overflow-x-auto p-4">
        <div className="flex gap-1.5 flex-wrap">
          {DAY_COLS.map((d) => {
            const val = row.days[d - 1] || 0;
            const hasVal = val > 0;
            return (
              <div
                key={d}
                title={hasVal ? `Day ${d}: ${money(val)}` : `Day ${d}: No class`}
                className={`flex h-9 w-9 flex-col items-center justify-center rounded-lg text-[10px] font-black transition ${
                  hasVal
                    ? "bg-primary text-white shadow-sm shadow-primary/30"
                    : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600"
                }`}
              >
                <span>{d}</span>
                {hasVal && (
                  <span className="text-[8px] opacity-75 leading-tight">✓</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Mini summary */}
        <div className="mt-4 flex flex-wrap gap-6 text-sm text-slate-500">
          <span>
            <b className="text-slate-800 dark:text-slate-200">{activeDays.length}</b> active days
          </span>
          <span>
            Avg per day:{" "}
            <b className="text-slate-800 dark:text-slate-200">
              {activeDays.length > 0 ? money(row.total / activeDays.length) : "—"}
            </b>
          </span>
        </div>
      </div>
    </DataCard>
  );
}
